// frontend/console-web/src/pages/Campaigns/gantt/gantt-math.ts
// Pure positioning math for the campaign Gantt. No React, no I/O.
// Bars + axis render in local browser time; off-hours shading boundaries
// are computed in the campaign window's timezone (matches the scheduler).

export type Zoom = "day" | "3day" | "week";

interface EngWindow {
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
}

const PAD_FRACTION = 0.05;

export function windowBounds(engagements: EngWindow[]): { start: Date; end: Date } {
  const starts = engagements
    .map((e) => e.scheduled_start_at)
    .filter((s): s is string => !!s)
    .map((s) => new Date(s).getTime());
  const ends = engagements
    .map((e) => e.scheduled_end_at)
    .filter((s): s is string => !!s)
    .map((s) => new Date(s).getTime());
  if (starts.length === 0 || ends.length === 0) {
    const now = Date.now();
    return { start: new Date(now), end: new Date(now + 24 * 3600 * 1000) };
  }
  const minStart = Math.min(...starts);
  const maxEnd = Math.max(...ends);
  const span = Math.max(maxEnd - minStart, 3600 * 1000);
  const pad = span * PAD_FRACTION;
  return { start: new Date(minStart - pad), end: new Date(maxEnd + pad) };
}

export function pctLeft(t: Date, start: Date, spanMs: number): number {
  const p = ((t.getTime() - start.getTime()) / spanMs) * 100;
  return Math.min(100, Math.max(0, p));
}

export function pctWidth(s: Date, e: Date, start: Date, spanMs: number): number {
  const left = pctLeft(s, start, spanMs);
  const right = pctLeft(e, start, spanMs);
  return Math.max(0, right - left);
}

export function nowLinePct(now: Date, start: Date, spanMs: number): number | null {
  const t = now.getTime();
  if (t < start.getTime() || t > start.getTime() + spanMs) return null;
  return ((t - start.getTime()) / spanMs) * 100;
}

export interface AxisColumn {
  label: string;
  leftPct: number;
  widthPct: number;
  showLabel: boolean;
}

export function dayColumns(start: Date, end: Date, zoom: Zoom, labelEvery = 1): AxisColumn[] {
  const spanMs = end.getTime() - start.getTime();
  const step = labelEvery < 1 ? 1 : Math.floor(labelEvery);
  const cols: AxisColumn[] = [];
  // walk calendar days from the start's local midnight
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  let guard = 0;
  let idx = 0;
  while (cur.getTime() < end.getTime() && guard < 400) {
    const next = new Date(cur);
    next.setDate(next.getDate() + 1);
    const segStart = Math.max(cur.getTime(), start.getTime());
    const segEnd = Math.min(next.getTime(), end.getTime());
    const leftPct = ((segStart - start.getTime()) / spanMs) * 100;
    const widthPct = ((segEnd - segStart) / spanMs) * 100;
    const label = cur.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
    // Label the first column always (it anchors the axis), then every `step`-th column.
    const showLabel = idx % step === 0;
    cols.push({ label, leftPct, widthPct, showLabel });
    cur.setDate(cur.getDate() + 1);
    guard += 1;
    idx += 1;
  }
  // zoom currently affects pixel density at the call site (container width),
  // not the column model — all zooms share day columns. Returned for all zooms.
  void zoom;
  return cols;
}

export interface Band {
  leftPct: number;
  widthPct: number;
}

function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map((x) => parseInt(x, 10));
  return h * 60 + m;
}

// Off-hours = the complement of [dailyStart,dailyEnd) on operating weekdays,
// plus all of non-operating weekdays. Boundaries computed in `tz`.
export function offHoursBands(
  start: Date,
  end: Date,
  dailyStart: string,
  dailyEnd: string,
  businessDays: number[],
  tz: string,
): Band[] {
  const spanMs = end.getTime() - start.getTime();
  const startMin = hhmmToMinutes(dailyStart);
  const endMin = hhmmToMinutes(dailyEnd); // "24:00" → 1440
  const bands: Band[] = [];
  // iterate each calendar day in tz
  const cur = new Date(start);
  cur.setHours(0, 0, 0, 0);
  let guard = 0;
  const pushBand = (a: number, b: number) => {
    const segStart = Math.max(a, start.getTime());
    const segEnd = Math.min(b, end.getTime());
    if (segEnd <= segStart) return;
    bands.push({
      leftPct: ((segStart - start.getTime()) / spanMs) * 100,
      widthPct: ((segEnd - segStart) / spanMs) * 100,
    });
  };
  while (cur.getTime() < end.getTime() && guard < 400) {
    const dayStart = cur.getTime();
    const dow = weekdayInTz(cur, tz); // 0=Mon..6=Sun
    const dayMidnightNext = dayStart + 24 * 3600 * 1000;
    if (!businessDays.includes(dow)) {
      pushBand(dayStart, dayMidnightNext); // whole non-operating day shaded
    } else {
      // shade [00:00, dailyStart) and [dailyEnd, 24:00)
      if (startMin > 0) pushBand(dayStart, dayStart + startMin * 60 * 1000);
      if (endMin < 1440) pushBand(dayStart + endMin * 60 * 1000, dayMidnightNext);
    }
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
  return bands;
}

// Weekday (0=Mon..6=Sun) of a Date as observed in `tz`.
function weekdayInTz(d: Date, tz: string): number {
  const wd = new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: tz }).format(d);
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return map[wd] ?? 0;
}
